import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Megaphone, Loader2, ChevronRight, ChevronLeft, Send, Calendar, Trash2, X, Upload, CheckCheck, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import StatusBadge from '@/components/StatusBadge';

function CampaignWizard({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [audMode, setAudMode] = useState('contacts');
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [searchContact, setSearchContact] = useState('');
  const [media, setMedia] = useState(null); // { media_id, media_type, filename }
  const [uploading, setUploading] = useState(false);
  const [varValues, setVarValues] = useState([]); // [{ index, source, value }]
  const [scheduleAt, setScheduleAt] = useState('');
  const [busy, setBusy] = useState(false);

  const template = templates.find((t) => t.id === templateId);
  const headerType = (template?.header_type || 'NONE').toUpperCase();
  const needsMedia = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setName('');
    setTemplateId('');
    setSelectedContacts(new Set());
    setSelectedGroups(new Set());
    setMedia(null);
    setVarValues([]);
    setScheduleAt('');
    Promise.all([
      api.get('/templates/'),
      api.get('/contacts/'),
      api.get('/groups/'),
    ]).then(([t, c, g]) => {
      setTemplates((t.data || []).filter((x) => x.status === 'APPROVED'));
      setContacts(c.data || []);
      setGroups(g.data || []);
    }).catch(() => toast.error('Failed to load wizard data'));
  }, [open]);

  useEffect(() => {
    if (!template) { setVarValues([]); return; }
    const n = template.variables_count || 0;
    setVarValues(Array.from({ length: n }, (_, i) => ({ index: i + 1, source: i === 0 ? 'contact_name' : 'static', value: '' })));
  }, [templateId]);

  const filteredContacts = useMemo(() => {
    const s = searchContact.toLowerCase();
    return s ? contacts.filter((c) => c.name?.toLowerCase().includes(s) || c.phone?.toLowerCase().includes(s)) : contacts;
  }, [contacts, searchContact]);

  const toggleSet = (set, setter, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  const uploadMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/campaigns/upload-media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMedia(r.data);
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const audienceCount = audMode === 'contacts' ? selectedContacts.size : groups.filter((g) => selectedGroups.has(g.id)).reduce((a, b) => a + (b.member_count || 0), 0);

  const stepValid = () => {
    if (step === 0) return !!templateId && name.trim().length >= 2;
    if (step === 1) return audMode === 'contacts' ? selectedContacts.size > 0 : selectedGroups.size > 0;
    if (step === 2) return needsMedia ? !!media : true;
    return true;
  };

  const create = async (sendNow) => {
    setBusy(true);
    try {
      const payload = {
        name,
        template_id: templateId,
        contact_ids: audMode === 'contacts' ? [...selectedContacts] : [],
        group_ids: audMode === 'groups' ? [...selectedGroups] : [],
        media_id: media?.media_id || null,
        media_url: null,
        media_type: media?.media_type || null,
        variable_values: varValues,
        scheduled_at: !sendNow && scheduleAt ? new Date(scheduleAt).toISOString() : null,
      };
      const r = await api.post('/campaigns/', payload);
      const cid = r.data.id;
      if (sendNow) {
        await api.post(`/campaigns/${cid}/send-now`);
        toast.success('Campaign sending! View progress below.');
      } else if (scheduleAt) {
        toast.success(`Scheduled for ${new Date(scheduleAt).toLocaleString()}`);
      } else {
        toast.success('Campaign saved as DRAFT');
      }
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const stepTitles = ['Template', 'Audience', needsMedia ? 'Media + Variables' : 'Variables', 'Send / Schedule'];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/40 sm:max-w-3xl rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="campaigns-create-wizard">
        <DialogHeader>
          <DialogTitle>Create Campaign</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
          {stepTitles.map((t, i) => (
            <div key={i} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border ${i === step ? 'bg-[#FFE2D1]/80 text-[#9A3412] border-[#FDBA74]' : i < step ? 'bg-[#EAF8F1] text-[#0F6B45] border-[#86EFAC]' : 'bg-white/60 text-muted-foreground border-white/60'}`}>
              {i + 1}. {t}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Campaign Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Diwali Goa Promo — Wave 1" className="bg-white/70 border-white/60 rounded-xl" data-testid="campaign-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Approved Template</Label>
              {templates.length === 0 ? (
                <div className="glass p-3 rounded-xl text-sm text-muted-foreground">No APPROVED templates yet. Submit one from the Templates page first.</div>
              ) : (
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger data-testid="campaign-template-select" className="bg-white/70 border-white/60 rounded-xl"><SelectValue placeholder="Select a template" /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.language} · {t.header_type}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {template && (
                <div className="mt-2 glass p-3 rounded-xl text-sm">
                  <div className="text-xs uppercase text-muted-foreground mb-1">Body</div>
                  <div className="whitespace-pre-wrap">{template.body_text}</div>
                  <div className="text-xs text-muted-foreground mt-2">{template.variables_count} variable(s) · Header: {template.header_type}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <Tabs value={audMode} onValueChange={setAudMode}>
              <TabsList className="bg-white/60 rounded-xl">
                <TabsTrigger value="contacts" data-testid="campaign-audience-contacts-tab">Pick Contacts</TabsTrigger>
                <TabsTrigger value="groups" data-testid="campaign-audience-groups-tab">Pick Groups</TabsTrigger>
              </TabsList>
              <TabsContent value="contacts">
                <Input placeholder="Search contacts..." value={searchContact} onChange={(e) => setSearchContact(e.target.value)} className="bg-white/70 border-white/60 rounded-xl mb-2" data-testid="campaign-contact-search" />
                <ScrollArea className="h-[280px] rounded-xl border border-white/50 bg-white/40">
                  <div className="p-2">
                    {filteredContacts.length === 0 ? (
                      <div className="text-center py-8 text-sm text-muted-foreground">No contacts</div>
                    ) : filteredContacts.map((c) => (
                      <label key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/60 cursor-pointer">
                        <Checkbox checked={selectedContacts.has(c.id)} onCheckedChange={() => toggleSet(selectedContacts, setSelectedContacts, c.id)} data-testid={`campaign-contact-checkbox-${c.id}`} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{c.name}</div>
                          <div className="text-xs text-muted-foreground">{c.phone}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </ScrollArea>
                <div className="text-xs text-muted-foreground">{selectedContacts.size} contact(s) selected</div>
              </TabsContent>
              <TabsContent value="groups">
                <ScrollArea className="h-[280px] rounded-xl border border-white/50 bg-white/40">
                  <div className="p-2">
                    {groups.length === 0 ? (
                      <div className="text-center py-8 text-sm text-muted-foreground">No groups created</div>
                    ) : groups.map((g) => (
                      <label key={g.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/60 cursor-pointer">
                        <Checkbox checked={selectedGroups.has(g.id)} onCheckedChange={() => toggleSet(selectedGroups, setSelectedGroups, g.id)} data-testid={`campaign-group-checkbox-${g.id}`} />
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: g.color }} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{g.name}</div>
                          <div className="text-xs text-muted-foreground">{g.member_count || 0} members</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </ScrollArea>
                <div className="text-xs text-muted-foreground">{selectedGroups.size} group(s) selected (≈{audienceCount} contacts)</div>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {needsMedia && (
              <div className="glass p-4 rounded-xl space-y-2">
                <Label>Header Media ({headerType})</Label>
                <div className="flex items-center gap-2">
                  <input type="file" onChange={uploadMedia} accept={headerType === 'IMAGE' ? 'image/*' : headerType === 'VIDEO' ? 'video/*' : '.pdf,application/pdf'} data-testid="campaign-media-input" className="block w-full text-sm rounded-xl border border-white/60 bg-white/60 p-2.5 file:mr-3 file:rounded-lg file:border-0 file:bg-orange-50 file:px-3 file:py-1.5 file:text-orange-800" />
                  {uploading && <Loader2 className="h-5 w-5 animate-spin" />}
                </div>
                {media && (
                  <div className="text-xs text-[#0F6B45] flex items-center gap-1"><CheckCheck className="h-3.5 w-3.5" /> Uploaded: {media.filename} ({media.media_type})</div>
                )}
              </div>
            )}
            {varValues.length > 0 ? (
              <div className="glass p-4 rounded-xl space-y-3">
                <Label>Variable Values</Label>
                {varValues.map((v, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="text-sm font-mono pt-2">{`{{${v.index}}}`}</div>
                    <Select value={v.source} onValueChange={(val) => setVarValues((prev) => prev.map((p, idx) => idx === i ? { ...p, source: val } : p))}>
                      <SelectTrigger className="bg-white/70 border-white/60 rounded-xl" data-testid={`campaign-var-source-${v.index}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="contact_name">Contact Name</SelectItem>
                        <SelectItem value="contact_phone">Contact Phone</SelectItem>
                        <SelectItem value="static">Static Value</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input disabled={v.source !== 'static'} placeholder={v.source === 'static' ? 'Enter value' : 'Auto from contact'} value={v.value} onChange={(e) => setVarValues((prev) => prev.map((p, idx) => idx === i ? { ...p, value: e.target.value } : p))} className="bg-white/70 border-white/60 rounded-xl" data-testid={`campaign-var-value-${v.index}`} />
                  </div>
                ))}
              </div>
            ) : !needsMedia ? (
              <div className="glass p-4 rounded-xl text-sm text-muted-foreground">This template has no variables. Skip to next step.</div>
            ) : null}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="glass p-4 rounded-xl space-y-2 text-sm">
              <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{name}</span></div>
              <div><span className="text-muted-foreground">Template:</span> <span className="font-mono">{template?.name}</span></div>
              <div><span className="text-muted-foreground">Audience:</span> <span className="font-medium">{audMode === 'contacts' ? `${selectedContacts.size} contacts` : `${selectedGroups.size} groups (~${audienceCount})`}</span></div>
              {needsMedia && media && <div><span className="text-muted-foreground">Media:</span> <span className="font-medium">{media.filename}</span></div>}
            </div>
            <div className="space-y-1.5">
              <Label>Schedule for later (optional)</Label>
              <Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="bg-white/70 border-white/60 rounded-xl" data-testid="campaign-schedule-datetime" />
              <div className="text-[11px] text-muted-foreground">Leave empty to send now or save as draft.</div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-row">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl bg-white/60 border-white/60 mr-auto"><X className="h-4 w-4 mr-1.5" />Cancel</Button>
          {step > 0 && <Button variant="outline" onClick={() => setStep((s) => s - 1)} className="rounded-xl bg-white/60 border-white/60" data-testid="campaigns-wizard-back"><ChevronLeft className="h-4 w-4 mr-1.5" />Back</Button>}
          {step < 3 && <Button onClick={() => setStep((s) => s + 1)} disabled={!stepValid()} className="gradient-orange text-white rounded-xl" data-testid="campaigns-wizard-next">Next <ChevronRight className="h-4 w-4 ml-1.5" /></Button>}
          {step === 3 && (
            <>
              {scheduleAt ? (
                <Button onClick={() => create(false)} disabled={busy} className="gradient-orange text-white rounded-xl" data-testid="campaign-schedule-button">
                  {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Calendar className="h-4 w-4 mr-1.5" />}Schedule
                </Button>
              ) : (
                <Button onClick={() => create(true)} disabled={busy} className="gradient-orange text-white rounded-xl" data-testid="campaign-send-now-button">
                  {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}Send Now
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/campaigns/');
      setCampaigns(r.data || []);
    } catch {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto refresh sending campaigns every 5 sec
  useEffect(() => {
    const hasSending = campaigns.some((c) => c.status === 'SENDING');
    if (!hasSending) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [campaigns]);

  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try {
      await api.delete(`/campaigns/${c.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const cancelCampaign = async (c) => {
    if (!window.confirm(`Cancel scheduled campaign "${c.name}"?`)) return;
    try {
      await api.post(`/campaigns/${c.id}/cancel`);
      toast.success('Cancelled');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cancel failed');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="campaigns-heading">Campaigns</h1>
          <p className="text-sm text-muted-foreground mt-1">Send approved templates to your audiences — now or scheduled.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="gradient-orange text-white rounded-xl" data-testid="campaigns-create-button">
          <Plus className="h-4 w-4 mr-1.5" /> New Campaign
        </Button>
      </div>

      {loading ? (
        <div className="glass-card p-10 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
      ) : !campaigns.length ? (
        <div className="glass-card p-10 text-center">
          <div className="inline-flex h-12 w-12 rounded-2xl bg-[#FFE2D1] text-[#9A3412] items-center justify-center mb-3"><Megaphone className="h-6 w-6" /></div>
          <div className="text-sm text-muted-foreground mb-3">No campaigns yet. Create your first WhatsApp marketing campaign.</div>
          <Button onClick={() => setOpen(true)} className="gradient-orange text-white rounded-xl">Create Campaign</Button>
        </div>
      ) : (
        <div className="glass-card overflow-hidden" data-testid="campaigns-table">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead className="bg-white/40 border-b border-white/50">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 hidden md:table-cell">Template</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Progress</th>
                  <th className="px-4 py-3 hidden xl:table-cell">Scheduled</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => {
                  const tpl = c.templates;
                  return (
                    <tr key={c.id} className="border-b border-white/30 hover:bg-white/30 transition-colors">
                      <td className="px-4 py-3"><Link to={`/campaigns/${c.id}`} className="font-medium hover:underline" data-testid={`campaign-name-${c.id}`}>{c.name}</Link></td>
                      <td className="px-4 py-3 hidden md:table-cell text-muted-foreground font-mono text-xs">{tpl?.name || '—'}</td>
                      <td className="px-4 py-3"><StatusBadge status={c.status} kind="campaign" /></td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="text-xs">{(c.sent_count ?? 0)}/{(c.total_count ?? 0)} sent · {(c.failed_count ?? 0)} failed</div>
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell text-xs">{c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/campaigns/${c.id}`}><Button variant="ghost" size="sm" data-testid={`campaign-view-${c.id}`}>View</Button></Link>
                        {c.status === 'SCHEDULED' && (
                          <Button variant="ghost" size="sm" onClick={() => cancelCampaign(c)} data-testid={`campaign-cancel-${c.id}`}>Cancel</Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => deleteCampaign(c)} aria-label="Delete" data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-4 w-4 text-[#9F1239]" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CampaignWizard open={open} onOpenChange={setOpen} onCreated={load} />
    </div>
  );
}
